import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
    Loader2, CheckCircle, SplitSquareVertical, Radio, RefreshCcw,
    HandMetal, Cog, Brain, Award, Sparkles, ChevronRight, Clock, Bell
} from 'lucide-react';
import { toast, Toaster } from 'sonner';
import { SENDAFooter } from '../components/SENDAFooter';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CH4_MODULES = [
    { id: 'descomposicion_momento', name: 'Descomposición del momento', icon: SplitSquareVertical, color: '#3E5F6E' },
    { id: 'microsenales', name: 'Mapa de microseñales', icon: Radio, color: '#7A9BA8' },
    { id: 'reescritura', name: 'Reescritura sin juicio', icon: RefreshCcw, color: '#E8B27A' },
    { id: 'intervencion_vivo', name: 'Intervención en vivo', icon: HandMetal, color: '#5FAF9D' },
    { id: 'automatizacion', name: 'Automatización', icon: Cog, color: '#5FAF9D' }
];

export default function ResumenCapitulo4() {
    const navigate = useNavigate();
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchProfile = async () => {
            try {
                const { data } = await axios.get(`${API_URL}/api/user/profile`, { withCredentials: true });
                setProfile(data);
            } catch {
                toast.error('Error al cargar datos');
                navigate('/usuario');
            } finally { setLoading(false); }
        };
        fetchProfile();
    }, [navigate]);

    if (loading) return <div className="min-h-screen bg-[#F5F7F8] flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#3E5F6E]" /></div>;

    const completedModules = profile?.modules_completed || [];
    const ch4Completed = CH4_MODULES.filter(m => completedModules.some(cm => cm.module_id === m.id));
    const completionRate = (ch4Completed.length / CH4_MODULES.length) * 100;
    const aiSummaries = profile?.ai_summaries || [];
    const habitPlan = profile?.habit_plan;

    const descomposicionSummary = aiSummaries.find(s => s.module === 'descomposicion_momento');
    const microsenalesSummary = aiSummaries.find(s => s.module === 'microsenales');
    const automatizacionSummary = aiSummaries.find(s => s.module === 'automatizacion');

    return (
        <div className="min-h-screen bg-[#F5F7F8]">
            <Toaster position="top-right" richColors />
            <header className="bg-white/90 backdrop-blur-xl border-b border-[#DCE3E8]/50 sticky top-0 z-50">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <img src="/logo-senda.png" alt="SENDA" className="h-9 w-auto" />
                            <div>
                                <h1 className="text-xl font-semibold text-[#1F2A30] font-['Outfit']" data-testid="ch4-summary-title">Resumen Capítulo 4</h1>
                                <p className="text-sm text-[#4A5B63]">Intervención y automatización</p>
                            </div>
                        </div>
                        <Link to="/usuario"><Button variant="ghost" className="text-[#3E5F6E] hover:bg-[#3E5F6E]/5">Volver al inicio</Button></Link>
                    </div>
                </div>
            </header>

            <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Hero */}
                <div className="relative overflow-hidden bg-gradient-to-br from-[#3E5F6E] via-[#2F4A57] to-[#1F2A30] rounded-3xl p-8 lg:p-12 mb-8 shadow-2xl">
                    <div className="absolute top-0 right-0 w-64 h-64 bg-[#5FAF9D]/10 rounded-full blur-3xl" />
                    <div className="absolute bottom-0 left-0 w-48 h-48 bg-[#E8B27A]/10 rounded-full blur-3xl" />
                    <div className="relative z-10 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
                        <div className="flex-1">
                            <div className="flex items-center gap-2 mb-4">
                                <Award className="h-6 w-6 text-[#5FAF9D]" />
                                <span className="text-[#5FAF9D] font-medium uppercase tracking-wide text-sm">Capítulo 4 Completado</span>
                            </div>
                            <h2 className="text-3xl lg:text-4xl font-light text-white font-['Outfit'] mb-4">Tu sistema de intervención está activo</h2>
                            <p className="text-white/70 text-lg max-w-xl">Has descompuesto tus momentos de bloqueo, identificado señales tempranas y diseñado un plan automático de acción.</p>
                        </div>
                        <div className="flex flex-col items-center">
                            <div className="relative w-40 h-40">
                                <svg className="w-full h-full transform -rotate-90">
                                    <circle cx="80" cy="80" r="70" fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="12" />
                                    <circle cx="80" cy="80" r="70" fill="none" stroke="#5FAF9D" strokeWidth="12" strokeLinecap="round" strokeDasharray={`${completionRate * 4.4} 440`} className="transition-all duration-1000" />
                                </svg>
                                <div className="absolute inset-0 flex flex-col items-center justify-center">
                                    <span className="text-4xl font-bold text-white font-['Outfit']">{Math.round(completionRate)}%</span>
                                    <span className="text-white/50 text-sm">completado</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
                    {/* Module Progress */}
                    <Card className="bg-white border-[#DCE3E8] shadow-sm">
                        <CardHeader><CardTitle className="flex items-center gap-2 text-[#1F2A30]"><Cog className="h-5 w-5 text-[#3E5F6E]" />Progreso por módulo</CardTitle></CardHeader>
                        <CardContent>
                            <div className="space-y-4">
                                {CH4_MODULES.map((module) => {
                                    const isCompleted = completedModules.some(cm => cm.module_id === module.id);
                                    const Icon = module.icon;
                                    return (
                                        <div key={module.id} className="flex items-center gap-4" data-testid={`ch4-module-${module.id}`}>
                                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isCompleted ? 'bg-[#5FAF9D]' : 'bg-[#DCE3E8]'}`}>
                                                {isCompleted ? <CheckCircle className="h-5 w-5 text-white" /> : <Icon className="h-5 w-5 text-[#7A8A93]" />}
                                            </div>
                                            <div className="flex-1">
                                                <div className="flex items-center justify-between mb-1">
                                                    <span className={`text-sm font-medium ${isCompleted ? 'text-[#1F2A30]' : 'text-[#7A8A93]'}`}>{module.name}</span>
                                                    <span className={`text-xs ${isCompleted ? 'text-[#5FAF9D]' : 'text-[#7A8A93]'}`}>{isCompleted ? '100%' : '0%'}</span>
                                                </div>
                                                <div className="h-2 bg-[#DCE3E8] rounded-full overflow-hidden">
                                                    <div className="h-full bg-[#5FAF9D] transition-all duration-500" style={{ width: isCompleted ? '100%' : '0%' }} />
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </CardContent>
                    </Card>

                    {/* Key Insights */}
                    <Card className="bg-white border-[#DCE3E8] shadow-sm">
                        <CardHeader><CardTitle className="flex items-center gap-2 text-[#1F2A30]"><Sparkles className="h-5 w-5 text-[#5FAF9D]" />Hallazgos clave</CardTitle></CardHeader>
                        <CardContent className="space-y-3">
                            {descomposicionSummary?.insight_usuario && (
                                <div className="p-3 bg-[#F5F7F8] rounded-xl border border-[#DCE3E8]">
                                    <p className="text-xs font-semibold text-[#3E5F6E] uppercase mb-1">Descomposición</p>
                                    <p className="text-sm text-[#1F2A30] italic">"{descomposicionSummary.insight_usuario}"</p>
                                </div>
                            )}
                            {microsenalesSummary?.insight_usuario && (
                                <div className="p-3 bg-[#F5F7F8] rounded-xl border border-[#DCE3E8]">
                                    <p className="text-xs font-semibold text-[#7A9BA8] uppercase mb-1">Microseñales</p>
                                    <p className="text-sm text-[#1F2A30] italic">"{microsenalesSummary.insight_usuario}"</p>
                                </div>
                            )}
                            {automatizacionSummary?.probabilidad_sostenibilidad && (
                                <div className="p-3 bg-gradient-to-r from-[#5FAF9D]/10 to-transparent rounded-xl border border-[#5FAF9D]/20">
                                    <p className="text-xs font-semibold text-[#5FAF9D] uppercase mb-1">Sostenibilidad del plan</p>
                                    <p className="text-sm text-[#1F2A30] font-medium capitalize">{automatizacionSummary.probabilidad_sostenibilidad}</p>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </div>

                {/* Habit Plan Card */}
                {habitPlan && (
                    <Card className="bg-white border-[#DCE3E8] shadow-sm mb-8" data-testid="habit-plan-card">
                        <CardHeader><CardTitle className="flex items-center gap-2 text-[#1F2A30]"><Cog className="h-5 w-5 text-[#5FAF9D]" />Tu Plan de Intervención Automática</CardTitle></CardHeader>
                        <CardContent>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                <div className="p-4 bg-[#D07F7E]/5 rounded-xl border border-[#D07F7E]/20">
                                    <p className="text-xs font-semibold text-[#D07F7E] uppercase mb-2">SI detecto...</p>
                                    <p className="text-sm text-[#1F2A30]">{habitPlan.disparador_microsenal}</p>
                                </div>
                                <div className="p-4 bg-[#3E5F6E]/5 rounded-xl border border-[#3E5F6E]/20">
                                    <p className="text-xs font-semibold text-[#3E5F6E] uppercase mb-2">Nombro</p>
                                    <p className="text-sm text-[#1F2A30]">"{habitPlan.etiqueta_nombre}"</p>
                                </div>
                                <div className="p-4 bg-[#5FAF9D]/5 rounded-xl border border-[#5FAF9D]/20">
                                    <p className="text-xs font-semibold text-[#5FAF9D] uppercase mb-2">Y ejecuto</p>
                                    <p className="text-sm text-[#1F2A30]">{habitPlan.accion_elegida}</p>
                                </div>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                                <div className="p-3 bg-[#F5F7F8] rounded-lg flex items-center gap-3">
                                    <Clock className="h-4 w-4 text-[#3E5F6E]" />
                                    <div>
                                        <p className="text-xs text-[#7A8A93]">Frecuencia</p>
                                        <p className="text-sm font-medium text-[#1F2A30]">{habitPlan.frecuencia}</p>
                                    </div>
                                </div>
                                <div className="p-3 bg-[#F5F7F8] rounded-lg flex items-center gap-3">
                                    <Bell className="h-4 w-4 text-[#E8B27A]" />
                                    <div>
                                        <p className="text-xs text-[#7A8A93]">Recordatorio</p>
                                        <p className="text-sm font-medium text-[#1F2A30]">{habitPlan.recordatorio}</p>
                                    </div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Final AI Insight */}
                {automatizacionSummary?.insight_usuario && (
                    <Card className="bg-white border-[#DCE3E8] shadow-sm mb-8">
                        <CardContent className="pt-6">
                            <div className="flex items-start gap-3">
                                <div className="w-10 h-10 bg-[#5FAF9D]/20 rounded-full flex items-center justify-center shrink-0">
                                    <Brain className="h-5 w-5 text-[#5FAF9D]" />
                                </div>
                                <div>
                                    <p className="text-xs font-semibold text-[#5FAF9D] uppercase mb-2">Reflexión final del capítulo</p>
                                    <p className="text-[#1F2A30] italic">"{automatizacionSummary.insight_usuario}"</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Back to dashboard */}
                <div className="text-center">
                    <Link to="/usuario">
                        <Button className="bg-[#3E5F6E] hover:bg-[#2F4A57] text-white px-8 py-3 rounded-xl" data-testid="back-to-dashboard-ch4">
                            Volver al inicio <ChevronRight className="h-4 w-4 ml-2" />
                        </Button>
                    </Link>
                </div>
            </main>
            <SENDAFooter />
        </div>
    );
}
